'use client';

import { useState, useEffect } from 'react';
import useSWR from 'swr';
import { getDocumentImageByUrl } from '@/publicSearch/api';

/**
 * Fetches a document image URL with X-Society-Id header and returns a blob URL
 * suitable for use in <img src>. Use for document image URLs from GraphQL (lot grid,
 * animal screen) so the document service receives the society context.
 */
export function useDocumentImageWithSociety(
  imageUrl: string | null | undefined,
  societyId: string | number | null | undefined,
) {
  const societyIdStr = societyId != null ? String(societyId) : undefined;
  const key =
    imageUrl && societyIdStr
      ? ['documentImageWithSociety', imageUrl, societyIdStr]
      : null;

  const {
    data: blob,
    isLoading,
    error,
  } = useSWR(key, () => getDocumentImageByUrl(imageUrl!, societyIdStr!), {
    revalidateOnFocus: false,
  });

  const [imgSrc, setImgSrc] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (imgSrc) {
        URL.revokeObjectURL(imgSrc);
        setImgSrc(null);
      }
    };
  }, [imgSrc]);

  useEffect(() => {
    if (!imageUrl || !societyIdStr) {
      setImgSrc(null);
      return;
    }
    if (blob && blob.size > 0) {
      const objectUrl = URL.createObjectURL(blob);
      setImgSrc(objectUrl);
    } else {
      setImgSrc(null);
    }
  }, [imageUrl, societyIdStr, blob]);

  return {
    imgSrc: imageUrl && societyIdStr ? imgSrc : null,
    isLoading,
    error,
  };
}
